import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Fingerprint, Send, Inbox, CheckCircle2, XCircle, Server, Cpu, Radio, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmt(s) {
  if (!s) return "never";
  try { return new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); }
  catch { return "—"; }
}

const EVENT_TINT = {
  project_created: "#8B949E", sow_ready: "#EAE04A", assessment_ready: "#FF7A59",
  config_started: "#00F0FF", config_passed: "#00FF41", delivery_ready: "#00FF41", config_failed: "#FF3B30",
};

const Stat = ({ label, value, color }) => (
  <div className="border border-white/10 bg-[#07080B] px-4 py-3 flex-1 min-w-[92px]" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="font-mono-jb text-2xl font-extrabold tracking-tighter" style={{ color }}>{value}</div>
    <div className="text-[9px] uppercase tracking-[0.2em] text-[#8B949E] font-mono-jb mt-0.5">{label}</div>
  </div>
);

export default function Integrations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/integration-health"); setData(data); }
    catch { toast.error("Failed to load integration health"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const ccReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/cc-reset");
      toast.success(`Cleared ${data.discoveries_deleted} discoveries, ${data.cc_projects_deleted} CC projects, ${data.jobs_deleted} jobs, ${data.cc_events_deleted} events.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setResetting(false); }
  };

  const cc = data?.cc, cg = data?.config_generator;

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Admin</div>
          <h1 className="font-mono-jb text-4xl sm:text-5xl font-extrabold tracking-tighter text-white flex items-center gap-3">
            <Activity className="text-[#00F0FF]" size={38} /> Integrations<span className="text-[#00F0FF] cursor-blink">_</span>
          </h1>
          <p className="text-[#8B949E] text-sm mt-3 max-w-2xl leading-relaxed">
            Live health of the two outward links — <span className="text-[#B084FF]">Command Centre</span> and the <span className="text-[#00F0FF]">Config Generator</span>. Both share one API key; compare the fingerprint below with each partner's <code className="text-[#00FF41]">/ping</code> to confirm they match.
          </p>
        </div>
        <Button data-testid="refresh-health" onClick={load} variant="outline" className="rounded-sm bg-transparent border-white/15 text-white font-mono-jb text-[11px] uppercase tracking-wider h-10 hover:bg-white/5 hover:text-[#00F0FF]">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Shared key fingerprint */}
      <div className="border border-[#00FF41]/25 bg-[#00FF41]/5 px-5 py-4 mb-8 flex items-center gap-3" data-testid="key-fingerprint">
        <Fingerprint className="text-[#00FF41]" size={22} />
        <div>
          <div className="font-mono-jb text-sm text-white">Shared key fingerprint: <span className="text-[#00FF41] font-bold tracking-wider">{loading ? "…" : (data?.key_fingerprint || "not set")}</span></div>
          <div className="text-[11px] text-[#8B949E] font-mono-jb mt-0.5">This is sha256(key)[:8] — safe to share. On each partner run <code>echo -n "&lt;key&gt;" | sha256sum | cut -c1-8</code>; equal = same secret.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Command Centre */}
        <div className="border border-white/10 bg-[#0B0C10] p-6" data-testid="cc-health-card">
          <div className="flex items-center gap-2 mb-4">
            <Server size={18} className="text-[#B084FF]" />
            <h2 className="font-mono-jb text-lg font-bold text-white">Command Centre</h2>
            <span className={`ml-auto text-[9px] uppercase tracking-[0.15em] font-mono-jb px-2 py-1 rounded-sm border ${cc?.webhook_configured ? "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" : "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10"}`}>
              {cc?.webhook_configured ? "Webhook push ON" : "Polling only"}
            </span>
          </div>
          <div className="flex gap-2 mb-4">
            <Stat label="Events" value={cc?.total_events ?? "—"} color="#00F0FF" />
            <Stat label="Delivered" value={cc?.delivered ?? "—"} color="#00FF41" />
            <Stat label="Undelivered" value={cc?.undelivered ?? "—"} color={cc?.undelivered ? "#EAE04A" : "#8B949E"} />
          </div>
          <div className="font-mono-jb text-[11px] text-[#8B949E] mb-2 flex items-center gap-1.5"><Send size={12} /> Last event: {fmt(cc?.last_event_at)}</div>
          <div className="font-mono-jb text-[11px] mb-3 flex items-center gap-1.5" data-testid="cc-heartbeat">
            <span className={`inline-block w-2 h-2 rounded-full ${cc?.inbound_healthy ? "bg-[#00FF41]" : "bg-[#FF3B30]"}`} />
            <span className="text-[#8B949E]">Heartbeat (≤10 min): {fmt(cc?.last_inbound_ping)}</span>
            {cc?.fingerprint_match === true && <span className="text-[#00FF41]">· key ✓</span>}
            {cc?.fingerprint_match === false && <span className="text-[#FF3B30]">· key ✗</span>}
          </div>
          <div className="border-t border-white/10 pt-3 max-h-[320px] overflow-auto" data-testid="cc-events-table">
            {cc?.recent?.length ? cc.recent.map((e, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 text-[11px] font-mono-jb border-b border-white/5">
                <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider" style={{ color: EVENT_TINT[e.event] || "#8B949E", border: `1px solid ${(EVENT_TINT[e.event] || "#8B949E")}55` }}>{e.event}</span>
                <span className="text-[#8B949E] truncate flex-1">{e.opportunity_id}</span>
                <span className="text-[#484F58]">{fmt(e.at)}</span>
                {e.delivered ? <CheckCircle2 size={13} className="text-[#00FF41]" /> : <XCircle size={13} className="text-[#EAE04A]" />}
              </div>
            )) : <div className="text-[#484F58] font-mono-jb text-[11px] py-3">No events yet. Once CC creates an opportunity they'll appear here.</div>}
          </div>
        </div>

        {/* Config Generator */}
        <div className="border border-white/10 bg-[#0B0C10] p-6" data-testid="cg-health-card">
          <div className="flex items-center gap-2 mb-4">
            <Cpu size={18} className="text-[#00F0FF]" />
            <h2 className="font-mono-jb text-lg font-bold text-white">Config Generator</h2>
            <span className="ml-auto text-[9px] uppercase tracking-[0.15em] font-mono-jb px-2 py-1 rounded-sm border text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10 flex items-center gap-1"><Radio size={10} /> netconfig-gen</span>
          </div>
          <div className="flex gap-2 mb-4 flex-wrap">
            <Stat label="Jobs" value={cg?.total_jobs ?? "—"} color="#00F0FF" />
            <Stat label="Pending" value={cg?.pending ?? "—"} color="#EAE04A" />
            <Stat label="Passed" value={cg?.passed ?? "—"} color="#00FF41" />
            <Stat label="Failed" value={cg?.failed ?? "—"} color={cg?.failed ? "#FF3B30" : "#8B949E"} />
          </div>
          <div className="font-mono-jb text-[11px] text-[#8B949E] mb-2 flex items-center gap-1.5"><Inbox size={12} /> Last job: {fmt(cg?.last_job_at)}</div>
          <div className="font-mono-jb text-[11px] mb-3 flex items-center gap-1.5" data-testid="cg-heartbeat">
            <span className={`inline-block w-2 h-2 rounded-full ${cg?.inbound_healthy ? "bg-[#00FF41]" : "bg-[#FF3B30]"}`} />
            <span className="text-[#8B949E]">Heartbeat (≤10 min): {fmt(cg?.last_inbound_ping)}</span>
            {cg?.fingerprint_match === true && <span className="text-[#00FF41]">· key ✓</span>}
            {cg?.fingerprint_match === false && <span className="text-[#FF3B30]">· key ✗</span>}
          </div>
          <div className="border-t border-white/10 pt-3 max-h-[320px] overflow-auto" data-testid="cg-jobs-table">
            {cg?.recent?.length ? cg.recent.map((j, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 text-[11px] font-mono-jb border-b border-white/5">
                <span className="text-[#00FF41]">{j.ps_code}</span>
                <span className="text-[#8B949E] truncate flex-1">{j.client || "—"}</span>
                <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider border border-white/15 text-[#8B949E]">{j.result || j.stage}</span>
                <span className="text-[#484F58]">{fmt(j.updated_at)}</span>
              </div>
            )) : <div className="text-[#484F58] font-mono-jb text-[11px] py-3">No jobs yet. Jobs appear after a project is marked WON and handed to netconfig-gen.</div>}
          </div>
        </div>
      </div>

      {/* Inbound audit — real CC calls to our discovery/project endpoints */}
      <div className="mt-8 border border-white/10 bg-[#0B0C10] p-5" data-testid="cc-inbound-log">
        <div className="font-mono-jb text-sm text-white flex items-center gap-2 mb-1"><Inbox size={16} className="text-[#B084FF]" /> Inbound from Command Centre</div>
        <div className="text-[11px] text-[#8B949E] font-mono-jb mb-3">Proof of the actual status/project calls CC made — with the fingerprint of the key they sent and whether it matched ours ({loading ? "…" : (data?.key_fingerprint || "not set")}).</div>
        <div className="space-y-1">
          {(data?.cc_inbound_recent || []).length ? data.cc_inbound_recent.map((r, i) => (
            <div key={i} className="flex items-center gap-2 font-mono-jb text-[11px] border-b border-white/5 py-1.5">
              {r.matched ? <CheckCircle2 size={12} className="text-[#00FF41] shrink-0" /> : <XCircle size={12} className="text-[#FF3B30] shrink-0" />}
              <span className="text-[#8B949E] w-28 shrink-0">{r.kind}</span>
              <span className="text-white truncate flex-1">{r.opportunity_id || "—"}{r.status ? ` → ${r.status}` : ""}</span>
              <span className={`px-1.5 py-0.5 rounded-sm text-[9px] border ${r.matched ? "border-[#00FF41]/30 text-[#00FF41]" : "border-[#FF3B30]/30 text-[#FF5C5C]"}`}>key {r.key_fp || "none"} {r.matched ? "✓" : "✗ 401"}</span>
              <span className="text-[#484F58]">{fmt(r.at)}</span>
            </div>
          )) : <div className="text-[#484F58] font-mono-jb text-[11px] py-3">No inbound CC calls logged yet. When CC posts a status update or creates a project, it appears here — including rejected (401) calls, so you can compare the key fingerprint they sent against ours.</div>}
        </div>
      </div>

      {/* Danger zone — clean test reset */}
      <div className="mt-8 border border-[#FF3B30]/25 bg-[#FF3B30]/5 p-5" data-testid="cc-reset-zone">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-jb text-sm text-[#FF5C5C] font-bold flex items-center gap-2"><Trash2 size={16} /> Reset CC test data</div>
            <div className="text-[11px] text-[#8B949E] font-mono-jb mt-1 max-w-xl">Deletes ALL discovery jobs, all config jobs, and every Command-Centre-originated project (and their docs/events). Manually-created projects are kept. Use this for a clean end-to-end test — ask CC to clear their side too.</div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="cc-reset-btn" disabled={resetting} variant="outline" className="rounded-sm bg-transparent border-[#FF3B30]/40 text-[#FF5C5C] font-mono-jb text-[11px] uppercase tracking-wider h-10 hover:bg-[#FF3B30]/10 hover:text-[#FF5C5C]">
                <Trash2 size={14} className="mr-1.5" /> {resetting ? "Clearing…" : "Clear all discovery jobs"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0B0C10] border-white/15">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-mono-jb text-white">Clear all CC test data?</AlertDialogTitle>
                <AlertDialogDescription className="font-mono-jb text-[#8B949E] text-xs">
                  This permanently deletes every discovery job and every Command-Centre-originated project on this environment. This cannot be undone. Manually-created projects are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="font-mono-jb text-xs bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="cc-reset-confirm" onClick={ccReset} className="font-mono-jb text-xs bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90">Yes, clear everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
